/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { EngagementForm } from '../../components/EngagementForm/EngagementForm'
import Loader from '../../components/Loader/Loader'
import { useGetAllClientsQuery } from '../../redux/client.endpoints'
import {
  useLazyGetEngagementByIdQuery,
  useUpdateEngagementMutation,
} from '../../redux/engagement.endpoints'
import { useGetAllTeamsQuery } from '../../redux/team.endpoints'
import { Engagement, UpdateEngagementDto } from '../../types/Engagement'

interface Props {
  companyId: number
}

const EngagementEdit = ({ companyId }: Props) => {
  const navigate = useNavigate()

  const { engagementId } = useParams()
  const [getEngagement, getEngagementResponse] = useLazyGetEngagementByIdQuery()
  const [engagement, setEngagement] = useState<Engagement>({} as Engagement)

  useEffect(() => {
    getEngagement({
      id: parseInt(engagementId || '0'),
      companyId,
    })
  }, [])

  useEffect(() => {
    if (getEngagementResponse.data) {
      setEngagement(getEngagementResponse.data)
    }
    if (getEngagementResponse.error) {
      navigate('/engagements')
    }
  }, [getEngagementResponse])

  const {
    data: clients,
    isSuccess: clientsSuccess,
    refetch: refetchClients,
  } = useGetAllClientsQuery(`${companyId}`)
  const {
    data: teams,
    isSuccess: teamsSuccess,
    refetch: refetchTeams,
  } = useGetAllTeamsQuery(companyId)
  const [updateEngagement, updateEngagementResult] =
    useUpdateEngagementMutation()
  const handleEditEngagement = async (args: UpdateEngagementDto) => {
    updateEngagement(args)
  }

  useEffect(() => {
    refetchClients()
    refetchTeams()
  }, [])

  useEffect(() => {
    if (updateEngagementResult && updateEngagementResult.isError) {
      if (updateEngagementResult.error) {
        const error = updateEngagementResult.error
        if ('data' in error) {
          const errorMessage = error.data as any

          if ('error' in errorMessage) {
            toast.error(errorMessage.error, {
              toastId: 'edit-engagement-error',
            })
          }
        }
      } else {
        toast.error(`An error occurred when updating the engagement.`, {
          toastId: 'edit-engagement-error',
        })
      }
    } else if (updateEngagementResult && updateEngagementResult.isSuccess) {
      toast.success(`Engagement updated successfully!`, {
        toastId: 'edit-engagement-success',
      })
      navigate(-1)
    }
  }, [updateEngagementResult])

  return (
    <div className="page-content engagement-form-page">
      <Helmet title="Time Off - Update Engagement" />
      <h1 className="engagement-form-title">Edit Engagement</h1>
      {teamsSuccess && clientsSuccess ? (
        <EngagementForm
          handleEditSubmit={handleEditEngagement}
          displayErrorBanner={
            updateEngagementResult && updateEngagementResult.isError
          }
          isDuplicateNameError={
            (updateEngagementResult?.error as any)?.status === 409
          }
          loading={updateEngagementResult && updateEngagementResult.isLoading}
          companyId={companyId}
          clients={clients || []}
          teams={teams || []}
          engagement={engagement}
        />
      ) : (
        <Loader />
      )}
    </div>
  )
}

export default EngagementEdit
